import { useEffect, useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import type { ScrapperEventStats } from './types';
import { EVENT_TYPE_LABELS } from './types';

interface Props {
  refreshKey: number;
  onFilterFailed: () => void;
}

const formatDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function ScrapperEventsStats({ refreshKey, onFilterFailed }: Props) {
  const [stats, setStats] = useState<ScrapperEventStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminHttpClient
      .get<ScrapperEventStats>(getAdminEndpointUrl('scrapperEvents', 'stats'))
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch((err) => {
        console.error('Error cargando stats de scrapper events', err);
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const successPct = stats ? Math.round(stats.successRate24h * 100) : 0;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
      {/* Tarjeta principal: estado del sistema */}
      <div className="md:col-span-2 relative overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 text-slate-100 shadow-xl">
        {/* grid texture */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        <div className="relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span
                  className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${
                    stats && stats.failedToday > 0 ? 'bg-rose-500' : stats && stats.processing > 0 ? 'bg-cyan-400' : 'bg-emerald-400'
                  }`}
                />
                <span
                  className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
                    stats && stats.failedToday > 0 ? 'bg-rose-500' : stats && stats.processing > 0 ? 'bg-cyan-400' : 'bg-emerald-400'
                  }`}
                />
              </span>
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">Estado del pipeline</p>
            </div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500">últimas 24h</p>
          </div>

          <div className="mt-6 flex items-end gap-6">
            <div>
              <p className="font-mono text-6xl font-light tabular-nums tracking-tight text-white">
                {loading ? '…' : successPct}
                <span className="ml-1 text-2xl text-slate-500">%</span>
              </p>
              <p className="mt-1 text-xs text-slate-500">tasa de éxito</p>
            </div>

            <div className="ml-auto flex flex-col items-end gap-1">
              <p className="font-mono text-2xl font-light tabular-nums text-slate-200">{loading ? '—' : stats?.total24h ?? 0}</p>
              <p className="text-[11px] uppercase tracking-wider text-slate-500">eventos totales</p>
            </div>
          </div>

          {/* progress rail */}
          <div className="mt-5 h-1 overflow-hidden rounded-full bg-slate-800">
            <div
              className={`h-full transition-all duration-700 ${
                successPct >= 95 ? 'bg-emerald-400' : successPct >= 80 ? 'bg-amber-400' : 'bg-rose-500'
              }`}
              style={{ width: `${Math.max(2, successPct)}%` }}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-3 text-[11px] font-mono">
            <Spec label="completados" value={stats?.completed24h ?? 0} tone="emerald" />
            <Spec label="procesando" value={stats?.processing ?? 0} tone="cyan" />
            <Spec label="pendientes" value={stats?.pending ?? 0} tone="amber" />
            <Spec label="fallidos hoy" value={stats?.failedToday ?? 0} tone="rose" />
          </div>
        </div>
      </div>

      {/* Failed urgente */}
      <button
        onClick={onFilterFailed}
        className="group relative overflow-hidden rounded-2xl border border-rose-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md dark:border-rose-900/50 dark:bg-rose-950/20"
      >
        <div className="absolute right-0 top-0 h-32 w-32 -translate-y-1/3 translate-x-1/3 rounded-full bg-rose-500/10 blur-2xl" />
        <div className="relative">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px] text-rose-500">error</span>
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-rose-600 dark:text-rose-400">Fallidos sin atender</p>
          </div>
          <p className="mt-3 font-mono text-5xl font-light tabular-nums tracking-tight text-rose-700 dark:text-rose-300">
            {loading ? '…' : stats?.failed ?? 0}
          </p>
          <p className="mt-2 text-xs text-rose-600/80 dark:text-rose-400/80">
            {stats && stats.failed > 0 ? 'Click para filtrar y reintentar' : 'Sin eventos pendientes de revisión'}
          </p>
        </div>
      </button>

      {/* Top errores */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px] text-slate-500">monitoring</span>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600 dark:text-slate-400">Top error codes (24h)</p>
        </div>

        {loading && <p className="mt-4 text-xs text-slate-400">Cargando…</p>}

        {!loading && stats && stats.topErrors.length === 0 && (
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <span className="material-symbols-outlined text-[14px] text-emerald-500">check_circle</span>
            Sin errores registrados
          </div>
        )}

        {!loading && stats && stats.topErrors.length > 0 && (
          <ul className="mt-4 space-y-2">
            {stats.topErrors.map((err) => {
              const max = stats.topErrors[0]?.count ?? 1;
              const pct = Math.max(8, Math.round((err.count / max) * 100));
              return (
                <li key={err.errorCode} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <code className="font-mono text-[11px] text-rose-600 dark:text-rose-400">{err.errorCode}</code>
                    <span className="font-mono tabular-nums text-slate-600 dark:text-slate-300">{err.count}</span>
                  </div>
                  <div className="h-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                    <div className="h-full bg-rose-400/70 dark:bg-rose-500/70" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Distribución por tipo */}
      <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px] text-slate-500">analytics</span>
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600 dark:text-slate-400">Distribución por tipo (24h)</p>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-4">
          {(['daily', 'historical', 'accounts_by_code'] as const).map((type) => {
            const count = stats?.byEventType.find((b) => b.eventType === type)?.count ?? 0;
            const total = stats?.byEventType.reduce((acc, b) => acc + b.count, 0) ?? 0;
            const pct = total === 0 ? 0 : Math.round((count / total) * 100);
            return (
              <div key={type} className="rounded-xl border border-slate-100 bg-slate-50/50 p-3 dark:border-slate-800 dark:bg-slate-950/40">
                <p className="text-[10px] uppercase tracking-widest text-slate-500">{EVENT_TYPE_LABELS[type]}</p>
                <p className="mt-2 font-mono text-2xl font-light tabular-nums text-slate-800 dark:text-slate-100">{count}</p>
                <p className="mt-0.5 font-mono text-[11px] text-slate-400">{pct}%</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Últimas fallas */}
      <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px] text-rose-500">history</span>
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600 dark:text-slate-400">Últimas fallas</p>
          </div>
          {stats && stats.recentFailures.length > 0 && (
            <button onClick={onFilterFailed} className="font-mono text-[11px] text-rose-600 hover:underline dark:text-rose-400">
              ver todas →
            </button>
          )}
        </div>

        {loading && <p className="mt-4 text-xs text-slate-400">Cargando…</p>}

        {!loading && stats && stats.recentFailures.length === 0 && (
          <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
            <span className="material-symbols-outlined text-[14px] text-emerald-500">verified</span>
            Sin fallas recientes — todo en orden
          </div>
        )}

        {!loading && stats && stats.recentFailures.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {stats.recentFailures.map((f) => (
              <li key={f.id} className="flex items-center gap-3 rounded-lg border border-rose-100 bg-rose-50/40 px-3 py-2 text-xs dark:border-rose-900/40 dark:bg-rose-950/20">
                <span className="material-symbols-outlined text-[14px] text-rose-500">close</span>
                <code className="font-mono text-rose-700 dark:text-rose-400">{f.errorCode ?? 'sin código'}</code>
                {f.failedStep && <span className="rounded-md bg-rose-100 px-1.5 py-0.5 font-mono text-[10px] text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">{f.failedStep}</span>}
                <span className="truncate text-slate-600 dark:text-slate-400">{f.companyName ?? '—'} · {f.acquirerSlug || '—'}</span>
                <span className="ml-auto font-mono text-[10px] text-slate-400">{formatDateTime(f.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Spec({ label, value, tone }: { label: string; value: number; tone: 'emerald' | 'cyan' | 'amber' | 'rose' }) {
  const dot = {
    emerald: 'bg-emerald-400',
    cyan: 'bg-cyan-400',
    amber: 'bg-amber-400',
    rose: 'bg-rose-500',
  }[tone];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-800/60 px-2.5 py-1 text-slate-200 ring-1 ring-inset ring-slate-700/60">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      <span className="tabular-nums text-slate-100">{value}</span>
      <span className="text-slate-400">{label}</span>
    </span>
  );
}
